import { createContext, useContext, useMemo } from "react";
import { clsx } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";
import { jsx } from "react/jsx-runtime";
//#region src/tokens/tokens.ts
/**
* The Design_Token names of `@hurdl/design-system`, as one typed list (R9.1,
* R9.2).
*
* `src/tokens.css` declares the tokens and owns their values; this module names
* them. It deliberately holds no value: a colour, a length or a duration belongs
* in exactly one place, and that place is the stylesheet, where the cascade can
* theme it. What this module holds is the *set* — ten families, the Tailwind
* namespace each is declared under, the members each declares and the number of
* members each is required to reach — so that the token tests and the package
* documentation read one list rather than three copies of it.
*
* Names are composed rather than written out. `--color-hurdl-primary-background`
* is `--`, the Tailwind namespace, the shared `hurdl` segment (R9.3) and the
* member, in that order, and `tokenNameOf` is the only place that order is
* expressed. Composition is why the semantic colour families are derived from
* `SEMANTIC_COLOUR_ROLES` rather than listed twice: the backgrounds and the
* foregrounds are those roles with a suffix, and the five roles in
* `ROLES_WITHOUT_FOREGROUND` contribute a background alone (R9.2), so the roles
* are written once and the colour tokens follow.
*
* ── The role model ──────────────────────────────────────────────────────────
*
* The palette answers four different jobs, and a role belongs to exactly one of
* them. Conflating two of them is what made the previous palette unusable: it had
* one treatment — a pale tint with dark text — and asked it to serve both an alert
* banner and a button fill, so the primary button was a pale chip. ADR 0004
* records the rebuild.
*
* - **Action roles** are solid and saturated, with a light foreground on them:
*   `primary` and `destructive`. These are button fills. Each has a `-hover`
*   partner supplying the fill its hover state uses, which carries no foreground
*   of its own because the foreground stays the role's.
* - **Surface roles** are the surfaces content sits on: `neutral` is the page,
*   `card` is a raised surface, `muted` is a subdued one whose foreground is
*   genuinely dimmer, and `accent` is the hover surface of an otherwise
*   unfilled control.
* - **Status roles** are soft tints with dark text, which is the right treatment
*   for a message rather than a control: `informational`, `success`, `warning`
*   and `error`.
*
* The fourth job is a line: `border`, `input` and `focus` each declare a
* background and no foreground, because nothing is ever placed upon a one-pixel
* rule. They are three roles rather than one because they
* are held to different bounds — `input` bounds a control and must be perceivable
* against the surfaces it is drawn on, while `border` divides two regions of one
* surface and carries no such requirement. `src/tokens.css` records the measured
* ratios.
*
* `destructive` and `error` are both red and are not the same role. One is a
* solid fill for a button that deletes something; the other is a tint for a
* message reporting that something failed. `Alert` maps its public `destructive`
* tone onto the `error` tokens, so the tone a consumer names is unchanged.
*
* Nothing here is exported from `src/index.ts`. A consumer references a token
* through the Tailwind utility derived from it — `bg-hurdl-primary-background` —
* or through `var()`, never through a JavaScript string, so exporting the list
* would widen the package's public contract while giving a consumer nothing it
* can use. The readers are inside the package: the token tests, which parse
* `src/tokens.css` and compare it with this list, `utilities/cn.ts`, which derives
* the `tailwind-merge` theme extension from the scale families, and the package's
* own documentation, which enumerates the token set from it. The module also sits
* at `tokens/tokens.ts` rather than `tokens/index.ts`, so the platform-wide
* `src/<name>/index.ts` build convention does not turn it into a second package
* entry point, which R8.4 forbids.
*/
/** The one segment every Design_Token name carries, shared by all of them (R9.3). */
const TOKEN_PREFIX = "hurdl";
/**
* The fifteen semantic colour roles (R9.2), in the order `src/tokens.css`
* declares them: the two action roles and their hover partners, the four
* surfaces, the four status tints, and the three lines.
*/
const SEMANTIC_COLOUR_ROLES = [
	"primary",
	"primary-hover",
	"destructive",
	"destructive-hover",
	"neutral",
	"card",
	"muted",
	"accent",
	"informational",
	"success",
	"warning",
	"error",
	"border",
	"input",
	"focus"
];
/**
* The roles declaring a background and no foreground (R9.2).
*
* Two reasons, and both matter. `border`, `input` and `focus` are lines: nothing
* is ever placed upon a one-pixel rule, so a paired foreground would be a token
* with no use and a contrast bound with no meaning. `primary-hover` and
* `destructive-hover` are the hover fills of the two action roles, and the
* foreground on them stays the role's own — that is what makes a hover state a
* change of one colour rather than of two, and it is why the contrast of each
* action foreground is measured against the hover fill as well as the resting one.
*/
const ROLES_WITHOUT_FOREGROUND = [
	"border",
	"input",
	"focus",
	"primary-hover",
	"destructive-hover"
];
/** The ten roles carrying a foreground token, in the order the roles are declared. */
const FOREGROUND_ROLES = SEMANTIC_COLOUR_ROLES.filter((role) => !ROLES_WITHOUT_FOREGROUND.includes(role));
function backgroundMemberOf(role) {
	return `${role}-background`;
}
function foregroundMemberOf(role) {
	return `${role}-foreground`;
}
/**
* The ten Design_Token families, in the order `src/tokens.css` declares them.
*
* `requiredMemberCount` is what the requirements demand of the family, not what
* it happens to hold: R9.1 states a lower bound for each scale family — at least
* six spacing steps, three radii, one font family, five font sizes, two font
* weights, two line heights, three durations and three elevation steps — and R9.2
* states exact counts for the two colour families, fifteen backgrounds and ten
* foregrounds. The counts are therefore written out rather than read from
* `members.length`, which would make the number agree with the list by
* construction and assert nothing. Every family holds exactly its required count
* today, so a test comparing the two reads as an equality while remaining correct
* if a scale gains a step.
*/
const FAMILY_DEFINITIONS = {
	semanticBackground: {
		namespace: "color",
		members: SEMANTIC_COLOUR_ROLES.map(backgroundMemberOf),
		requiredMemberCount: 15
	},
	semanticForeground: {
		namespace: "color",
		members: FOREGROUND_ROLES.map(foregroundMemberOf),
		requiredMemberCount: 10
	},
	spacing: {
		namespace: "spacing",
		members: [
			"1",
			"2",
			"3",
			"4",
			"5",
			"6",
			"8",
			"10",
			"12",
			"16",
			"64"
		],
		requiredMemberCount: 6
	},
	radius: {
		namespace: "radius",
		members: [
			"small",
			"medium",
			"large"
		],
		requiredMemberCount: 3
	},
	fontFamily: {
		namespace: "font",
		members: ["sans", "display"],
		requiredMemberCount: 1
	},
	fontSize: {
		namespace: "text",
		members: [
			"caption",
			"body",
			"subheading",
			"heading",
			"display"
		],
		requiredMemberCount: 5
	},
	fontWeight: {
		namespace: "font-weight",
		members: ["regular", "semibold"],
		requiredMemberCount: 2
	},
	lineHeight: {
		namespace: "leading",
		members: ["tight", "relaxed"],
		requiredMemberCount: 2
	},
	duration: {
		namespace: "duration",
		members: [
			"fast",
			"medium",
			"slow"
		],
		requiredMemberCount: 3
	},
	/**
	* Elevation. Theme-independent, and so absent from the dark rule: a shadow is
	* a translucent black in both themes, and redeclaring it would create two
	* places to change one value. It reads as weaker on a dark surface, which is
	* what a shadow does.
	*/
	elevation: {
		namespace: "shadow",
		members: [
			"low",
			"medium",
			"high"
		],
		requiredMemberCount: 3
	}
};
/**
* The Design_Token name a namespace and a member compose. The one place the
* shape of a token name is expressed.
*/
function tokenNameOf(namespace, member) {
	return `--${namespace}-${TOKEN_PREFIX}-${member}`;
}
/** The background token of a semantic colour role, for all fifteen roles. */
function backgroundTokenOf(role) {
	return tokenNameOf("color", backgroundMemberOf(role));
}
/** The foreground token paired with a semantic colour role, for the ten roles that have one. */
function foregroundTokenOf(role) {
	return tokenNameOf("color", foregroundMemberOf(role));
}
/**
* A family with its token names composed from its namespace and its members.
*
* The type assertion is the one thing in this module taken on trust. Both
* operands are read from `FAMILY_DEFINITIONS`, so every composed name is a
* `DesignTokenName` by construction, but the definition arrives here widened to
* `FamilyDefinition` and TypeScript cannot follow the composition back to the
* literals. The composed set is checked against the declarations in
* `src/tokens.css` by the token tests, which is where that guarantee actually has
* to come from.
*/
function familyOf(definition) {
	return {
		namespace: definition.namespace,
		members: definition.members,
		requiredMemberCount: definition.requiredMemberCount,
		tokens: definition.members.map((member) => tokenNameOf(definition.namespace, member))
	};
}
/** The ten families with their token names composed, keyed by family name. */
const TOKEN_FAMILIES = {
	semanticBackground: familyOf(FAMILY_DEFINITIONS.semanticBackground),
	semanticForeground: familyOf(FAMILY_DEFINITIONS.semanticForeground),
	spacing: familyOf(FAMILY_DEFINITIONS.spacing),
	radius: familyOf(FAMILY_DEFINITIONS.radius),
	fontFamily: familyOf(FAMILY_DEFINITIONS.fontFamily),
	fontSize: familyOf(FAMILY_DEFINITIONS.fontSize),
	fontWeight: familyOf(FAMILY_DEFINITIONS.fontWeight),
	lineHeight: familyOf(FAMILY_DEFINITIONS.lineHeight),
	duration: familyOf(FAMILY_DEFINITIONS.duration),
	elevation: familyOf(FAMILY_DEFINITIONS.elevation)
};
Object.values(TOKEN_FAMILIES).flatMap((family) => family.tokens);
[...SEMANTIC_COLOUR_ROLES.map(backgroundTokenOf), ...FOREGROUND_ROLES.map(foregroundTokenOf)];
//#endregion
//#region src/utilities/cn.ts
/**
* Composes a `class` attribute value from a component's default classes and the
* `className` its consumer supplied (R10.4).
*
* `cn` is the shadcn/ui idiom — `twMerge(clsx(inputs))` — and it is the idiom
* because the two halves answer two different questions. `clsx` flattens whatever
* the call site passed: strings, arrays, and the `condition && 'class'` and
* `{ 'class': condition }` forms a component uses to make a class conditional.
* `twMerge` then resolves conflicts, so that the *last* utility of a Tailwind
* class group wins and the earlier ones are removed from the attribute
* altogether. Because every component passes its own classes first and the
* supplied `className` last, a supplied utility overrides the default it
* conflicts with, and every default it does not conflict with survives.
*
* That is a reversal. The Design_System previously composed class names by
* concatenation, in `utilities/merge-class-names.ts`, precisely so that no
* default could be dropped — and it recorded `tailwind-merge` as deliberately
* rejected for that reason. The reversal is deliberate in turn: concatenation
* left `className` unable to override anything, because CSS precedence between
* two rules of equal specificity is decided by their order in the *stylesheet*
* and never by their order in the `class` attribute. Overriding a default was
* therefore best-effort at best and misleading at worst. R10.4 has been amended
* to state the merge semantics, ADR 0003 records the reversal, and this is the
* implementation of the amended criterion.
*
* Not exported from `src/index.ts`. It is how the components are built rather
* than part of what the Design_System offers, and a consumer wanting the same
* helper has `clsx` and `tailwind-merge` directly.
*
* ── Why the merge has to be configured ──────────────────────────────────────
*
* `tailwind-merge` decides which group a class belongs to by parsing its name
* against Tailwind's *default* scales. This package's utilities are derived from
* its own Design_Token scales instead, and three families are misread as a result.
* All three were verified against `tailwind-merge` 3.6.0 with the unconfigured
* `twMerge`:
*
* - `text-hurdl-body` is a font size, but `hurdl-body` is not a member of
*   Tailwind's font size scale, so the class falls through to the text *colour*
*   group — whose validator accepts any value. A later `text-hurdl-neutral-foreground`
*   then looks like a conflict and the font size is dropped. `Card`, `Input` and
*   `Alert` each declare a font size and a text colour together, so each would
*   have silently lost its typography.
* - `font-hurdl-semibold` is a font weight, but Tailwind's font weight group
*   matches a fixed list of names — `medium`, `semibold` and the rest — which
*   `hurdl-semibold` is not in, so it falls through to the font *family* group
*   and drops the `font-hurdl-sans` beside it.
* - `shadow-hurdl-low` is a box shadow, but Tailwind's box shadow group matches a
*   t-shirt size, which `hurdl-low` is not, so it falls through to the shadow
*   *colour* group — the same any-value validator `text-hurdl-body` fell through
*   to. Two things went wrong there and neither was visible. A supplied
*   `shadow-primary`, which sets a shadow colour and not a shadow, silently
*   dropped the elevation beside it; and `shadow-none` and `shadow-sm`, which are
*   in the box shadow group, conflicted with nothing and left two box shadows in
*   one attribute for the stylesheet to decide between. `Card` and the three
*   filled `Button` variants each declare `shadow-hurdl-low`, so a consumer could
*   neither cancel nor replace it.
*
* Extending the theme with this package's own members fixes all three, and gives
* the remaining scales the conflict resolution they would otherwise not get at all:
* unextended, `px-hurdl-3` and `px-hurdl-8` are both unrecognised, so both
* survive and neither reliably wins. A consumer passing `p-hurdl-8` to override
* `p-hurdl-4` is the case R10.4 now promises, and it works only because the
* scales below are declared.
*
* One of Tailwind's conflict rules then has to be withdrawn, and it is the fourth
* finding. `tailwind-merge` treats a font size as displacing a line height,
* because Tailwind's own `--text-*` scale pairs each step with a
* `--text-*--line-height`, so `text-sm` sets both properties and resets whatever
* `leading-*` preceded it. This package's font size tokens declare no paired line
* height, so `text-hurdl-body` sets a font size and nothing else — and yet the
* unwithdrawn rule dropped `Button`'s `leading-hurdl-tight`, which its base
* classes declare before the size variant contributes a font size. The rule is
* therefore overridden to conflict with nothing.
*
* That override reaches Tailwind's own font sizes as well as this package's, so a
* consumer's `text-sm` no longer displaces a component's `leading-*` either. The
* trade is deliberate: the cost is that two line heights can reach one attribute,
* where the stylesheet decides between them, and the benefit is that a
* component's own line height cannot be silently discarded by its own font size.
*/
/**
* The `tailwind-merge` theme group each Design_Token family supplies members to.
*
* Derived from `tokens/tokens.ts` rather than written out, so that a step added
* to a scale is understood by the merge without a second edit here. The mapping
* is the interesting part and it is not one-to-one with the family names: this
* package names the family after the design concept — `fontSize`, `lineHeight` —
* while `tailwind-merge` names its theme groups after the Tailwind namespace the
* utilities come from, `text` and `leading`.
*
* Three of the ten families are absent, each for its own reason.
*
* The two semantic colour families need nothing. Tailwind's colour groups accept
* any value, so `bg-hurdl-primary-background` and `text-hurdl-neutral-foreground`
* are already classified correctly, and declaring them would change no outcome.
*
* The duration family cannot be declared, because `tailwind-merge` has no
* duration theme group — and it does not need to be. `--duration-*` is not a
* Tailwind namespace either, so components reference a duration through the
* CSS-variable shorthand `duration-(--duration-hurdl-medium)`, which is an
* arbitrary value and is classified as a duration on that basis alone.
* `src/tokens.css` records why the shorthand is used.
*
* Elevation is present, and was the fourth absence until the omission was tested
* rather than reasoned about. ADR 0004 added the family after this mapping was
* written and it was not extended with it, which looked harmless because a
* `shadow-hurdl-*` beside another one already resolved correctly — for the wrong
* reason. It is the third misreading and it is the same trapdoor as the first:
* `hurdl-low` is not a t-shirt size, so the class fell through to the shadow
* *colour* group, whose validator accepts any value, and two elevations therefore
* conflicted with each other as two colours would. Declaring the members is what
* puts them in the box shadow group instead. The doc comment above records what
* that changed and what it cost.
*/
const THEME_GROUP_BY_FAMILY = {
	spacing: "spacing",
	radius: "radius",
	fontFamily: "font",
	fontSize: "text",
	fontWeight: "font-weight",
	lineHeight: "leading",
	elevation: "shadow"
};
/**
* The theme members of one family, as `tailwind-merge` reads them: the value part
* of the utility, which is the shared prefix segment followed by the member.
*
* `--spacing-hurdl-3` yields the utility `px-hurdl-3`, so the member
* `tailwind-merge` has to recognise is `hurdl-3` — the token name minus its
* Tailwind namespace. That is the same composition `tokenNameOf` performs,
* without the namespace, and it is why nothing here restates a token name.
*/
function themeMembersOf(family) {
	return TOKEN_FAMILIES[family].members.map((member) => `${TOKEN_PREFIX}-${member}`);
}
/** The `theme` extension, one entry per family that maps to a theme group. */
const THEME_EXTENSION = Object.fromEntries(Object.entries(THEME_GROUP_BY_FAMILY).map(([family, themeGroup]) => [themeGroup, themeMembersOf(family)]));
/**
* The `tailwind-merge` class group whose conflict list is withdrawn, and the empty
* list it is replaced by.
*
* Named rather than written inline, because the entry is doing something subtle:
* it states that in this package's theme a font size utility sets a font size and
* nothing else, so it displaces no line height. The doc comment above records what
* that costs.
*/
const FONT_SIZE_CLASS_GROUP = "font-size";
/**
* Typed as the library's own class group identifiers rather than as `string`,
* because the configuration is typed that way: an empty list of the right element
* type is what says "this group displaces nothing" without asserting anything.
*/
const NO_CONFLICTING_GROUPS = [];
/**
* The merge this package uses: Tailwind's default configuration extended with the
* Design_Token scales, never replaced by them.
*
* `extend` for the theme, because a consumer's `className` is ordinary Tailwind —
* `w-full`, `mt-8`, `hidden` — and overriding a scale would leave those
* unrecognised and unable to override anything. The platform's scales are added to
* Tailwind's; both are understood.
*
* `override` for the one conflict rule, because `extend` appends to a list and the
* change wanted here is a removal.
*/
const twMerge = extendTailwindMerge({
	override: { conflictingClassGroups: { [FONT_SIZE_CLASS_GROUP]: NO_CONFLICTING_GROUPS } },
	extend: { theme: THEME_EXTENSION }
});
/**
* The `class` attribute value the arguments compose.
*
* ```ts
* cn('px-hurdl-3 py-hurdl-2', isInvalid && 'border-destructive', className);
* ```
*
* Falsy arguments are discarded by `clsx`, so a conditional class reads as
* `condition && 'class'` at the call site rather than forcing a ternary ending in
* an empty string.
*/
function cn(...inputs) {
	return twMerge(clsx(inputs));
}
//#endregion
//#region src/theming/theme.ts
/**
* The class that selects the dark values of whichever theme is in force.
*
* Declared in `src/tokens.css`, where the `dark` custom variant matches the
* element carrying it as well as its descendants, so it can be applied to a
* wrapper and covers everything rendered inside. There is no light counterpart to
* apply: the light mode is the absence of this class.
*/
const DARK_MODE_CLASS_NAME = "hurdl-dark";
/**
* The prefix every product theme's class carries.
*
* A convention rather than a mechanism — nothing enforces it, because the class is
* declared in the product's own stylesheet — but a shared one, so that a theme
* class is recognisable in rendered markup and cannot collide with a component's
* utility classes.
*/
const THEME_CLASS_NAME_PREFIX = "hurdl-theme-";
/** The reference theme this package ships, and the default of every consumer. */
const DEFAULT_THEME = { name: "Default" };
/** The default mode: the values an element under no dark class inherits. */
const DEFAULT_THEME_MODE = "light";
/**
* The classes that put an element and its descendants under a theme and a mode.
*
* Returns the theme's class when it has one and the dark class when the mode is
* dark, in that order, joined by a single space — and an empty string for the
* default theme in light mode, which is the case that needs no class at all.
*/
function themeClassNamesOf(theme, mode) {
	return [theme.className, mode === "dark" ? DARK_MODE_CLASS_NAME : void 0].filter((name) => name !== void 0).join(" ");
}
//#endregion
//#region src/theming/theme-scope.tsx
/**
* The default is the same default `ThemeSurface` renders under, so a component used
* with no surface above it at all behaves as it did before this existed: the theme
* block's own light values, selected by no class.
*/
const AmbientThemeContext = createContext({
	theme: DEFAULT_THEME,
	mode: DEFAULT_THEME_MODE
});
/**
* Publishes the theme and the mode a surface applied, for a {@link ThemeScope}
* beneath it to re-apply.
*
* Rendered by `ThemeSurface` and by nothing else. It carries the two values rather
* than the composed class names, so that the composition stays in one place —
* `themeClassNamesOf` — and a `ThemeScope` also has the theme's name and the mode to
* publish as attributes, which is what makes a portalled surface readable in an
* inspector and in a browser check.
*/
function ThemeScopeProvider({ theme, mode, children }) {
	const ambient = useMemo(() => ({
		theme,
		mode
	}), [theme, mode]);
	return /* @__PURE__ */ jsx(AmbientThemeContext.Provider, {
		value: ambient,
		children
	});
}
/**
* The contents of a portal, under the theme and mode of the surface the portal was
* rendered from (R11.5, R18.4).
*
* ```tsx
* <SelectPrimitive.Portal>
*   <ThemeScope>
*     <SelectPrimitive.Content …>…</SelectPrimitive.Content>
*   </ThemeScope>
* </SelectPrimitive.Portal>
* ```
*
* `display: contents` is the one presentational instruction it carries, and it is
* load-bearing: the element generates no box at all, so it can neither be a
* containing block for the `fixed` surfaces inside it nor add a row to the document
* it was appended to. What it does do is sit in the document tree, which is the only
* thing needed — custom properties inherit through an element with no box, so the
* theme's values reach the subtree while the layout is as though this element were
* not there.
*
* Every property is spread, and deliberately: both Radix portals render with
* `asChild`, so this element *is* the portalled child and receives the portal's own
* ref and properties through a slot. Dropping them would leave a dialog's presence
* detection without the node it watches.
*
* `data-theme` and `data-theme-mode` mirror what `ThemeSurface` renders, so a
* portalled surface says which theme it resolved against without anyone inferring it
* from a class list.
*
* Not exported from `src/index.ts`. A caller has no use for it — a component that
* portals uses it, and a component that does not needs nothing — and invariant 5 of
* `AGENTS.md` keeps the theming mechanics internal for the same reason it keeps `cn`
* internal.
*/
function ThemeScope({ className, ...attributes }) {
	const { theme, mode } = useContext(AmbientThemeContext);
	return /* @__PURE__ */ jsx("div", {
		...attributes,
		"data-slot": "theme-scope",
		"data-theme": theme.name,
		"data-theme-mode": mode,
		className: cn("contents", themeClassNamesOf(theme, mode), className)
	});
}
//#endregion
//#region src/theming/theme-surface.tsx
/**
* The classes that make a themed surface visible.
*
* Every value is a Design_Token reference: the page colour pair, the sans family,
* the body size and one step of the spacing scale. It exists because a theme that
* is not painted cannot be judged — components carrying their own background would
* otherwise sit on the user agent's white canvas, and a dark theme surrounded by
* white tells you nothing about the dark theme.
*
* Because the colours are token references and the tokens are what the theme class
* redeclares, this surface repaints itself when the theme or the mode changes
* without knowing that either happened.
*/
const SURFACE_CLASS_NAMES = [
	"p-hurdl-4",
	"font-hurdl-sans font-hurdl-regular text-hurdl-body",
	"bg-hurdl-neutral-background text-hurdl-neutral-foreground"
].join(" ");
/**
* A region rendered under a Theme and a Theme_Mode (R11.5).
*
* ```tsx
* <ThemeSurface>            (the default theme, light)
* <ThemeSurface mode="dark">
* <ThemeSurface theme={{ name: 'Acme', className: 'hurdl-theme-acme' }} />
* ```
*
* This is the one implementation of theme switching in the platform, and it is
* exported so that it stays the one implementation. The Design_System's fixture
* browser, every capability's fixture browser, every harness and every application
* apply a theme by rendering this rather than by each assembling the class names
* themselves — which is how a platform ends up with a dozen subtly different
* switchers and one of them forgetting the surface colours.
*
* It renders a `div` and spreads every property it was given, so a caller can set
* `id`, `style`, an `aria-` attribute or a handler, and `className` composes
* through `cn` with the supplied classes last (R10.4). Nesting is supported and
* meaningful: an inner surface's theme classes override an outer one's for its own
* subtree, because that is how the cascade resolves two ancestors declaring the
* same custom property.
*
* The class on that `div` reaches its descendants and nothing else, so the theme is
* also *published* through {@link ThemeScopeProvider}: a surface portalled out of
* the document tree — the open list of a `Select`, the surface and scrim of a
* `Dialog` — re-applies the same two classes around its own contents and resolves
* the same values. `theme-scope.tsx` records what went wrong before it did, in
* measured colours.
*
* `fill` is the one property that is not a design decision. `min-h-screen`
* references no Design_Token, because there is no viewport token to reference and
* none is wanted: it states that the surface fills the frame it was rendered in,
* which is a fact about the container rather than about the theme.
*/
function ThemeSurface({ theme = DEFAULT_THEME, mode = DEFAULT_THEME_MODE, fill = true, className, ...attributes }) {
	return /* @__PURE__ */ jsx(ThemeScopeProvider, {
		theme,
		mode,
		children: /* @__PURE__ */ jsx("div", {
			...attributes,
			"data-slot": "theme-surface",
			"data-theme": theme.name,
			"data-theme-mode": mode,
			className: cn(SURFACE_CLASS_NAMES, fill && "min-h-screen", themeClassNamesOf(theme, mode), className)
		})
	});
}
//#endregion
export { DEFAULT_THEME_MODE as a, cn as c, DEFAULT_THEME as i, ThemeScope as n, THEME_CLASS_NAME_PREFIX as o, DARK_MODE_CLASS_NAME as r, themeClassNamesOf as s, ThemeSurface as t };

//# sourceMappingURL=theme-surface-D671Y4Kc.mjs.map